import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { 
  Package, 
  Search, 
  Truck, 
  CheckCircle, 
  Clock, 
  MapPin, 
  Phone,
  Loader2,
  PackageCheck,
  PackageX,
  Home,
  CircleDot,
  ExternalLink,
  RefreshCw
} from "lucide-react";
import { format } from "date-fns";
import { PageHeader } from "@/components/shared/PageHeader";

interface TrackingEvent {
  id: string;
  status: string;
  location: string | null;
  description: string | null;
  timestamp: string;
}

interface OrderTracking {
  orderNumber: string;
  trackingNumber: string;
  status: string;
  estimatedDelivery: string | null;
  currentLocation: string | null;
  timeline: TrackingEvent[];
  shippingAddress?: {
    fullName: string;
    city: string;
    state: string;
  };
  totalAmount?: number;
  deliveryType?: string;
}

// Fez tracking portal URL
const FEZ_TRACKING_PORTAL = "https://track.fezdelivery.co";

const statusConfig: Record<string, { icon: any; color: string; label: string; bgColor: string }> = {
  order_placed: { 
    icon: Package, 
    color: 'text-blue-600', 
    label: 'Order Placed',
    bgColor: 'bg-blue-100'
  },
  confirmed: { 
    icon: CheckCircle, 
    color: 'text-green-600', 
    label: 'Confirmed',
    bgColor: 'bg-green-100'
  },
  processing: { 
    icon: Clock, 
    color: 'text-amber-600', 
    label: 'Processing',
    bgColor: 'bg-amber-100'
  },
  picked_up: { 
    icon: PackageCheck, 
    color: 'text-purple-600', 
    label: 'Picked Up',
    bgColor: 'bg-purple-100'
  },
  in_transit: { 
    icon: Truck, 
    color: 'text-indigo-600', 
    label: 'In Transit',
    bgColor: 'bg-indigo-100'
  },
  out_for_delivery: { 
    icon: Truck, 
    color: 'text-orange-600', 
    label: 'Out for Delivery',
    bgColor: 'bg-orange-100'
  },
  delivered: { 
    icon: Home, 
    color: 'text-green-600', 
    label: 'Delivered',
    bgColor: 'bg-green-100'
  },
  cancelled: { 
    icon: PackageX, 
    color: 'text-red-600', 
    label: 'Cancelled',
    bgColor: 'bg-red-100'
  },
  shipped: { 
    icon: Truck, 
    color: 'text-indigo-600', 
    label: 'Shipped',
    bgColor: 'bg-indigo-100'
  }
};

const getStatusConfig = (status: string) => {
  const normalizedStatus = status.toLowerCase().replace(/ /g, '_');
  return statusConfig[normalizedStatus] || statusConfig.processing;
};

const TrackOrder = () => {
  const { orderId } = useParams<{ orderId?: string }>();
  const [trackingNumber, setTrackingNumber] = useState(orderId || "");
  const [orderDetails, setOrderDetails] = useState<OrderTracking | null>(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [liveSource, setLiveSource] = useState<string | null>(null);

  useEffect(() => {
    if (orderId) {
      handleTrack();
    }
  }, [orderId]);

  const fetchFezLiveTracking = async (orderIdToTrack: string, trackNum: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('track-fez-order', {
        body: { order_id: orderIdToTrack, tracking_number: trackNum },
      });

      if (error || !data?.success) {
        console.log('Fez live tracking not available, using local data');
        return null;
      }

      return data;
    } catch (err) {
      console.error('Error fetching Fez live tracking:', err);
      return null;
    }
  };

  const handleRefreshFez = async () => {
    if (!orderDetails) return;
    setRefreshing(true);
    try {
      const fezData = await fetchFezLiveTracking(
        orderDetails.orderNumber.length === 8 ? '' : '',
        orderDetails.trackingNumber
      );

      // Try with the tracking number
      const result = await supabase.functions.invoke('track-fez-order', {
        body: { tracking_number: orderDetails.trackingNumber },
      });

      if (result.data?.success && result.data.tracking) {
        const t = result.data.tracking;
        setLiveSource(result.data.source);
        setOrderDetails(prev => prev ? {
          ...prev,
          status: t.status,
          trackingNumber: t.tracking_number || prev.trackingNumber,
          estimatedDelivery: t.estimated_delivery || prev.estimatedDelivery,
          timeline: t.events && t.events.length > 0 ? t.events.map((e: any) => ({
            id: e.id,
            status: e.status,
            description: e.description,
            location: e.location,
            timestamp: e.timestamp,
          })) : prev.timeline,
        } : prev);
      }
    } catch (err) {
      console.error('Refresh failed:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const handleTrack = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    if (!trackingNumber.trim()) return;

    setLoading(true);
    setSearched(true);
    setOrderDetails(null);
    setLiveSource(null);

    try {
      // First, try the Fez edge function (works for all users, even unauthenticated)
      const fezResult = await supabase.functions.invoke('track-fez-order', {
        body: { tracking_number: trackingNumber.trim(), order_id: trackingNumber.trim() },
      });

      if (fezResult.data?.success && fezResult.data.tracking) {
        const t = fezResult.data.tracking;
        setLiveSource(fezResult.data.source);

        const timeline: TrackingEvent[] = t.events && t.events.length > 0
          ? t.events.map((e: any) => ({
              id: e.id,
              status: e.status,
              location: e.location,
              description: e.description,
              timestamp: e.timestamp,
            }))
          : [{
              id: '1',
              status: t.status || 'processing',
              location: null,
              description: t.status_label || 'Order is being processed',
              timestamp: new Date().toISOString(),
            }];

        setOrderDetails({
          orderNumber: (t.order_id || '').slice(0, 8).toUpperCase() || 'N/A',
          trackingNumber: t.tracking_number || trackingNumber,
          status: t.status || 'processing',
          estimatedDelivery: t.estimated_delivery,
          currentLocation: null,
          timeline,
          shippingAddress: t.recipient_name ? {
            fullName: t.recipient_name,
            city: t.delivery_city || '',
            state: t.delivery_state || '',
          } : undefined,
          totalAmount: t.total_amount,
          deliveryType: t.delivery_type || 'fez',
        });
        return;
      }

      // Fallback: try local DB queries (requires auth for orders table)
      let trackingData = null;
      let orderData = null;

      const { data: trackingResult } = await supabase
        .from('tracking')
        .select('*')
        .or(`tracking_number.ilike.%${trackingNumber}%,order_id.eq.${trackingNumber}`)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (trackingResult) {
        trackingData = trackingResult;
        const { data: order } = await supabase
          .from('orders')
          .select('*')
          .eq('id', trackingResult.order_id)
          .single();
        orderData = order;
      } else {
        const { data: order } = await supabase
          .from('orders')
          .select('*')
          .or(`id.eq.${trackingNumber},tracking_number.ilike.%${trackingNumber}%,fez_tracking_number.ilike.%${trackingNumber}%`)
          .limit(1)
          .maybeSingle();

        if (order) {
          orderData = order;
          const { data: tracking } = await supabase
            .from('tracking')
            .select('*')
            .eq('order_id', order.id)
            .order('updated_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          trackingData = tracking;
        }
      }

      if (!orderData && !trackingData) {
        setOrderDetails(null);
        return;
      }

      const currentOrderId = orderData?.id || trackingData?.order_id;

      const { data: events } = await supabase
        .from('tracking_events')
        .select('*')
        .eq('order_id', currentOrderId)
        .order('timestamp', { ascending: false });

      let timeline: TrackingEvent[] = [];
      if (events && events.length > 0) {
        timeline = events.map(e => ({
          id: e.id,
          status: e.status,
          location: e.location,
          description: e.description,
          timestamp: e.timestamp
        }));
      } else {
        const currentStatus = trackingData?.status || orderData?.status || 'order_placed';
        timeline = [{
          id: '1',
          status: currentStatus,
          location: trackingData?.location || null,
          description: trackingData?.description || 'Order is being processed',
          timestamp: trackingData?.updated_at || orderData?.created_at || new Date().toISOString()
        }];
      }

      const shippingAddress = orderData?.shipping_address as any;

      setOrderDetails({
        orderNumber: currentOrderId?.slice(0, 8).toUpperCase() || 'N/A',
        trackingNumber: orderData?.fez_tracking_number || trackingData?.tracking_number || orderData?.tracking_number || `TRK${currentOrderId?.slice(0, 8).toUpperCase()}`,
        status: trackingData?.status || orderData?.status || 'processing',
        estimatedDelivery: trackingData?.estimated_delivery || orderData?.estimated_delivery,
        currentLocation: trackingData?.location || null,
        timeline,
        shippingAddress: shippingAddress ? {
          fullName: shippingAddress.fullName || orderData?.recipient_name || 'Customer',
          city: shippingAddress.city || orderData?.delivery_city || '',
          state: shippingAddress.state || orderData?.delivery_state || ''
        } : orderData?.recipient_name ? {
          fullName: orderData.recipient_name,
          city: orderData.delivery_city || '',
          state: orderData.delivery_state || ''
        } : undefined,
        totalAmount: orderData?.total_amount,
        deliveryType: orderData?.delivery_type || 'fez'
      });

    } catch (error) {
      console.error('Error tracking order:', error);
      setOrderDetails(null);
    } finally {
      setLoading(false);
    }
  };

  const currentStatusConfig = orderDetails ? getStatusConfig(orderDetails.status) : null;
  const StatusIcon = currentStatusConfig?.icon || Package;

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <PageHeader
        title="Track Your Order"
        description="Enter your tracking number or order ID to see the latest status"
        showBack
      />

      <Card className="p-6 mb-8">
        <form onSubmit={handleTrack} className="space-y-4">
          <div>
            <Label htmlFor="tracking">Tracking Number or Order ID</Label>
            <div className="flex gap-2 mt-1">
              <Input
                id="tracking"
                placeholder="Enter tracking number (e.g., SBXABC12345) or order ID"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                required
                className="flex-1"
              />
              <Button type="submit" disabled={loading} className="flex items-center gap-2">
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                Track
              </Button>
            </div>
          </div>
        </form>
      </Card>

      {loading && (
        <Card className="p-8 text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Searching for your order...</p>
        </Card>
      )}

      {!loading && orderDetails && (
        <div className="space-y-6">
          {/* Live Source Indicator */}
          {liveSource && (
            <div className="flex items-center justify-between">
              <Badge variant="outline" className="text-xs gap-1">
                {liveSource === 'fez_live' ? (
                  <><span className="w-2 h-2 rounded-full bg-green-500 animate-pulse inline-block" /> Live from Fez Delivery</>
                ) : liveSource === 'local_fallback' ? (
                  <><span className="w-2 h-2 rounded-full bg-yellow-500 inline-block" /> Cached Data</>
                ) : (
                  <><span className="w-2 h-2 rounded-full bg-muted-foreground inline-block" /> Local Data</>
                )}
              </Badge>
              {(orderDetails.deliveryType === 'fez' || orderDetails.trackingNumber.startsWith('FEZ')) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRefreshFez}
                  disabled={refreshing}
                  className="gap-2"
                >
                  {refreshing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                  Refresh
                </Button>
              )}
            </div>
          )}

          {/* Status Banner */}
          <Card className={`p-6 ${currentStatusConfig?.bgColor || 'bg-muted'}`}>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-full bg-white shadow-sm ${currentStatusConfig?.color}`}>
                  <StatusIcon className="h-8 w-8" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Current Status</p>
                  <h2 className={`text-2xl font-bold ${currentStatusConfig?.color}`}>
                    {currentStatusConfig?.label || orderDetails.status}
                  </h2>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Tracking Number</p>
                <p className="font-mono font-bold text-lg">{orderDetails.trackingNumber}</p>
                {/* Fez Tracking Portal Link */}
                {(orderDetails.deliveryType === 'fez' || orderDetails.trackingNumber.startsWith('FEZ')) && (
                  <a
                    href={`${FEZ_TRACKING_PORTAL}/${orderDetails.trackingNumber}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-2 text-sm text-primary hover:underline"
                  >
                    Track on Fez Portal
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </div>
          </Card>

          {/* Order Details */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Package className="h-5 w-5 text-muted-foreground" />
                Order Information
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Order Number</span>
                  <span className="font-medium">#{orderDetails.orderNumber}</span>
                </div>
                {orderDetails.totalAmount && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Order Value</span>
                    <span className="font-medium">₦{orderDetails.totalAmount.toLocaleString()}</span>
                  </div>
                )}
                {orderDetails.estimatedDelivery && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Estimated Delivery</span>
                    <span className="font-medium">
                      {format(new Date(orderDetails.estimatedDelivery), 'MMM d, yyyy')}
                    </span>
                  </div>
                )}
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <MapPin className="h-5 w-5 text-muted-foreground" />
                Delivery Location
              </h3>
              {orderDetails.shippingAddress ? (
                <div className="space-y-2">
                  <p className="font-medium">{orderDetails.shippingAddress.fullName}</p>
                  <p className="text-muted-foreground">
                    {orderDetails.shippingAddress.city}, {orderDetails.shippingAddress.state}
                  </p>
                  {orderDetails.currentLocation && (
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-sm text-muted-foreground">Current Location</p>
                      <p className="font-medium flex items-center gap-1">
                        <CircleDot className="h-3 w-3 text-primary" />
                        {orderDetails.currentLocation}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-muted-foreground">Address information not available</p>
              )}
            </Card>
          </div>

          {/* Timeline */}
          <Card className="p-6">
            <h3 className="font-semibold mb-6 flex items-center gap-2">
              <Clock className="h-5 w-5 text-muted-foreground" />
              Tracking Timeline
            </h3>
            <div className="relative">
              <div className="absolute left-4 top-8 bottom-8 w-0.5 bg-border"></div>
              <div className="space-y-6">
                {orderDetails.timeline.map((event, index) => {
                  const eventConfig = getStatusConfig(event.status);
                  const EventIcon = eventConfig.icon;
                  const isLatest = index === 0;
                  
                  return (
                    <div key={event.id} className="flex gap-4">
                      <div className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-full ${
                        isLatest ? eventConfig.bgColor : 'bg-muted'
                      }`}>
                        <EventIcon className={`h-4 w-4 ${isLatest ? eventConfig.color : 'text-muted-foreground'}`} />
                      </div>
                      <div className="flex-1 pb-2">
                        <div className="flex items-center gap-2">
                          <p className={`font-medium ${!isLatest && 'text-muted-foreground'}`}>
                            {eventConfig.label}
                          </p>
                          {isLatest && (
                            <Badge variant="secondary" className="text-xs">Latest</Badge>
                          )}
                        </div>
                        {event.description && (
                          <p className="text-sm text-muted-foreground mt-1">{event.description}</p>
                        )}
                        <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                          <span>{format(new Date(event.timestamp), 'MMM d, yyyy • h:mm a')}</span>
                          {event.location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {event.location}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>

          {/* Help Section */}
          <Card className="p-6 bg-muted/30">
            <p className="text-sm text-muted-foreground mb-3">Need help with your order?</p>
            <div className="flex flex-wrap gap-3">
              <Button variant="outline" size="sm" className="flex items-center gap-2">
                <Phone className="h-4 w-4" />
                Contact Seller
              </Button>
              <Button variant="outline" size="sm" className="flex items-center gap-2">
                Customer Support
              </Button>
            </div>
          </Card>
        </div>
      )}

      {!loading && searched && !orderDetails && (
        <Card className="p-8 text-center">
          <PackageX className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">Order Not Found</h3>
          <p className="text-muted-foreground mb-4">
            We couldn't find an order with tracking number "{trackingNumber}".
          </p>
          <p className="text-sm text-muted-foreground">
            Please check your tracking number and try again. You can also try using your order ID.
          </p>
        </Card>
      )}
    </div>
  );
};

export default TrackOrder;
